import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { I18n } from '@fiverr-private/i18n-react';
import { Text } from '@fiverr-private/typography';
import { regulationTranslation } from '@fiverr-private/price_regulation_translations';
import { priceMarkupFormatter } from '../../../GigCardListings/ListingsTransformer/priceTransformer';
import {
    PRICE_DEFAULT_LABELS,
    PRICE_LABEL_TYPES,
} from '../../../GigCardListings/ListingsTransformer/priceTransformer/constants';
import { useGigListingsContext } from '../../../GigCardListings/context/GigListingsContext';
import { useDisplayPrice } from '../../../hooks';
import { createClickedElement, trackGigForClickType } from '../../utils';
import isDefualtCurrency from '../../../GigCardListings/utils/defualtCurrency';
import ContextualLink from '../ContextualLink';
import { ELEMENTS_NAME, ELEMENTS_TYPE, OPEN_GIG_SAME_TAB_TARGET } from '../constants';
import TooltipWrapper from '../../../shared/components/TooltipWrapper';
import { PriceUpfrontTooltip } from '../../../shared/components/PriceUpfrontTooltip';
import './price.scss';

const Price = (props, context) => {
    const {
        url,
        originalPrice = 0,
        urlTarget,
        priceLabelType,
        showFrom,
        priceDisplayStatus,
        showHourlyPrice = false,
        hourlyRate,
    } = props;
    const { currencyObj, showAroundPriceLabel } = useGigListingsContext();
    const { name } = currencyObj;
    const fromLabel = showAroundPriceLabel ? PRICE_DEFAULT_LABELS.AROUND : PRICE_DEFAULT_LABELS.FROM;
    const defaultLabel = showFrom ? fromLabel : PRICE_DEFAULT_LABELS.STARTING_AT;
    const priceLabel = priceLabelType === PRICE_LABEL_TYPES.DEFAULT ? defaultLabel : priceLabelType;
    const { trackEvent } = context;

    const regulationTranslationText = regulationTranslation(priceDisplayStatus);

    const { displayPrice: price, showTooltip } = useDisplayPrice({
        originalPrice,
        hourlyRate,
        showHourlyPrice,
        currency: currencyObj,
    });

    if (showHourlyPrice && !hourlyRate) {
        return null;
    }

    const clickedPriceElement = clickedElement(price);

    return (
        <TooltipWrapper content={<PriceUpfrontTooltip />} shouldRenderTooltip={showTooltip} position="bottom">
            <ContextualLink
                href={url}
                target={urlTarget}
                className={classNames('price', { 'line-break': !isDefualtCurrency(name) })}
                onMouseDown={(e) => trackGigForClickType(e.button, trackEvent, clickedPriceElement)}
            >
                {!showHourlyPrice && priceLabel && (
                    <small className="text-body-3">
                        <I18n k={`gig_listings.gig_cards.price.${priceLabel}`} />
                    </small>
                )}
                <span dangerouslySetInnerHTML={{ __html: priceMarkupFormatter({ currencyObj, price }) }} />
                {showHourlyPrice && <I18n k="gig_listings.gig_cards.price.hourly.suffix" />}
                {regulationTranslationText && (
                    <Text paddingLeft="1.5" fontWeight="normal" color="bodySecondary" as="span">
                        {regulationTranslationText}
                    </Text>
                )}
            </ContextualLink>
        </TooltipWrapper>
    );
};

const PriceType = PropTypes.oneOfType([PropTypes.string.isRequired, PropTypes.number.isRequired]);

Price.propTypes = {
    url: PropTypes.string.isRequired,
    priceLabelType: PropTypes.string,
    urlTarget: PropTypes.string,
    originalPrice: PriceType,
    showFrom: PropTypes.bool,
    priceDisplayStatus: PropTypes.oneOf(['fees', 'taxes', 'both', 'none']),
    showHourlyPrice: PropTypes.bool,
    hourlyRate: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

Price.defaultProps = {
    urlTarget: OPEN_GIG_SAME_TAB_TARGET,
    priceDisplayStatus: 'none',
};

Price.contextTypes = {
    trackEvent: PropTypes.func.isRequired,
};

export const clickedElement = (price) => createClickedElement(ELEMENTS_NAME.GIG_PRICE, ELEMENTS_TYPE.LINK, price);

export default Price;
